import { Link } from 'react-router-dom';
import { PageShell } from '../shared/PageShell';

const POLICY_VERSION = '1.0.0';
const EFFECTIVE_DATE = 'March 4, 2026';
const LAST_UPDATED = 'March 4, 2026';

export function RefundPolicy() {
  return (
    <PageShell>
      <div className="max-w-3xl mx-auto px-6 py-12">
        {/* Header */}
        <div className="mb-2">
          <Link to="/" className="text-text-muted hover:text-text-primary transition-colors text-sm">
            &larr; Back
          </Link>
        </div>
        <h1 className="font-display text-3xl md:text-4xl font-bold text-gold mb-2">Refund &amp; Cancellation Policy</h1>
        <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm text-text-muted mb-3">
          <span>Version {POLICY_VERSION}</span>
          <span>Effective: {EFFECTIVE_DATE}</span>
          <span>Last Updated: {LAST_UPDATED}</span>
        </div>
        <p className="text-text-secondary text-base leading-relaxed mb-10">
          We want you to feel confident about your purchase. Below is a clear summary of when and how you can cancel your subscription or request a refund.
        </p>

        <div className="space-y-10">
          {/* §1 */}
          <Section id="subscription" number="1" title="How Your Subscription Works">
            <p>Your subscription begins when you complete and submit your intake form. At that point, you will be charged for your first month.</p>
            <p>Your subscription includes:</p>
            <ul>
              <li>Access to licensed healthcare providers (live consultations and electronic form reviews)</li>
              <li>Provider reviews for medications fulfilled through our pharmacy partners</li>
              <li>Lab services and care support</li>
              <li>24/7 patient support line</li>
              <li>Other health care services as part of your plan</li>
            </ul>
            <p>The specific services, labs, and medications included may vary depending on your subscription plan.</p>
          </Section>

          {/* §2 */}
          <Section id="cancellation" number="2" title="Cancellation Policy">
            <p>You can cancel your subscription at any time, for any reason. Here's how it works:</p>
            <ul>
              <li><strong>How to cancel:</strong> Contact our support team by email at <a href="mailto:support@hemologix.com" className="text-accent-light hover:underline">support@hemologix.com</a> or through your online account.</li>
              <li><strong>72-hour notice required:</strong> Your cancellation request must be received at least <strong>72 hours before</strong> your next billing date.</li>
              <li><strong>If you miss the 72-hour window:</strong> You will be charged on the upcoming billing date, and your cancellation will take effect on the following billing cycle.</li>
              <li><strong>Access continues:</strong> After cancellation, you will continue to have access to your services through the end of your current billing cycle.</li>
            </ul>
          </Section>

          {/* §3 */}
          <Section id="refund-eligibility" number="3" title="Refund Eligibility">
            <p>Refunds are available for your <strong>current billing cycle only</strong>, as long as your medication has <strong>not yet been ordered</strong>. Once medication is ordered, refunds are no longer available for that cycle.</p>
            <p>Here's when the refund window closes:</p>
            <ul>
              <li><strong>New patients (no video visit required):</strong> Your refund window closes when you submit your intake form.</li>
              <li><strong>New patients (video visit required):</strong> Your refund window closes when your video visit is completed.</li>
              <li><strong>Returning patients:</strong> Your refund window closes when you submit your refill intake for that billing cycle.</li>
            </ul>
            <div className="bg-bg-card border border-bg-tertiary rounded-xl p-5 mt-3">
              <p className="text-text-primary font-medium">Important: No retroactive refunds</p>
              <p className="text-text-secondary mt-1">Refunds apply only to your most recent billing cycle. Previous billing cycles are not eligible for refund.</p>
            </div>
          </Section>

          {/* §4 */}
          <Section id="prescription-medications" number="4" title="Prescription Medications">
            <p><strong>Federal law prohibits the return of prescription medications that have already been ordered or dispensed.</strong> Once your medication has been ordered from the pharmacy, a refund cannot be issued for that cycle.</p>
            <p>This is a legal requirement, not a HemoLogix policy — it applies to all prescription medications in the United States.</p>
          </Section>

          {/* §5 */}
          <Section id="bundle-pricing" number="5" title="Bundle Pricing Refunds">
            <p>If you purchased a multi-month bundle and are requesting a refund, here's how the calculation works:</p>
            <ul>
              <li>Your refund is <strong>prorated</strong> based on how many months of medication you have already received.</li>
              <li>The proration uses the <strong>regular (non-discounted) price</strong> per month, not the bundle discount price.</li>
            </ul>
            <p>For example, if you bought a 3-month bundle at a discount but have already received 1 month of medication, the refund is calculated by subtracting the regular single-month price from the bundle total.</p>
          </Section>

          {/* §6 */}
          <Section id="damaged-incorrect" number="6" title="Damaged or Incorrect Items">
            <p>If you receive damaged or incorrect medication:</p>
            <ul>
              <li><strong>Inspect your medication immediately</strong> when it arrives.</li>
              <li><strong>Contact us right away</strong> at <a href="mailto:support@hemologix.com" className="text-accent-light hover:underline">support@hemologix.com</a> — the sooner you reach out, the faster we can help.</li>
              <li>When possible, damaged or incorrect medications will be <strong>replaced by the pharmacy</strong>.</li>
              <li>Refunds for damaged or incorrect items are considered on a <strong>case-by-case basis</strong> and may require documentation (such as a photo of the damaged item).</li>
            </ul>
          </Section>

          {/* §7 */}
          <Section id="medical-disqualification" number="7" title="Medical Disqualification">
            <p>If our medical provider determines that you are not eligible for treatment for any medical reason, <strong>you will receive a full refund</strong>. No questions asked.</p>
            <p>Your health and safety come first — you should never be charged for a service you cannot safely receive.</p>
          </Section>

          {/* §8 */}
          <Section id="how-to-request" number="8" title="How to Request a Refund">
            <p>To request a refund or ask questions about your billing:</p>
            <div className="bg-bg-card border border-bg-tertiary rounded-xl p-5 mt-3">
              <p className="font-medium text-text-primary">Contact Support</p>
              <p className="text-text-muted mt-1">Email: <a href="mailto:support@hemologix.com" className="text-accent-light hover:underline">support@hemologix.com</a></p>
              <p className="text-text-muted mt-1">Or visit our <Link to="/support" className="text-accent-light hover:underline">Support page</Link> for additional help options.</p>
            </div>
            <p className="mt-3">We aim to respond to all refund requests within 2 business days.</p>
          </Section>
        </div>

        {/* Footer */}
        <div className="border-t border-bg-tertiary mt-12 pt-6 flex flex-wrap gap-4 text-sm text-text-muted">
          <Link to="/terms" className="hover:text-text-primary transition-colors">Terms of Service</Link>
          <Link to="/privacy" className="hover:text-text-primary transition-colors">Privacy Policy</Link>
          <Link to="/support" className="hover:text-text-primary transition-colors">Contact Support</Link>
        </div>
      </div>
    </PageShell>
  );
}

function Section({ id, number, title, children }: { id: string; number: string; title: string; children: React.ReactNode }) {
  return (
    <section id={id}>
      <h2 className="font-display text-xl md:text-2xl font-semibold text-text-primary mb-3">
        <span className="text-gold mr-2">§{number}</span>{title}
      </h2>
      <div className="text-text-secondary text-base leading-relaxed space-y-3 [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:space-y-2 [&_li]:leading-relaxed">
        {children}
      </div>
    </section>
  );
}
